// src/App.js
import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import Login from './components/Login';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import ProductManagement from './components/ProductManagement';
import CategoryManagement from './components/CategoryManagement';
import StockManagement from './components/StockManagement';
import InventoryCounts from './components/InventoryCounts';
import InventoryAdjustments from './components/InventoryAdjustments';
import SalesPurchases from './components/SalesPurchases';
import UserManagement from './components/UserManagement';
import ReportsAnalytics from './components/ReportsAnalytics';
import StockReport from './components/StockReport';
import SearchFilters from './components/SearchFilters';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';

// Error Boundary Component
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('App Error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="error-container">
                    <h1>Something went wrong.</h1>
                    <button onClick={() => window.location.reload()}>Reload App</button>
                </div>
            );
        }

        return this.props.children;
    }
}

// Loading Component
const Loading = () => (
    <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
    </div>
);

// Root redirect component
const RootRedirect = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        console.debug('[RootRedirect] Rendering:', { 
            isAuthenticated: !!user, 
            isLoading: loading
        });
        
        if (!loading) {
            if (user) {
                console.debug('[RootRedirect] User authenticated, redirecting to dashboard');
                navigate('/admin/dashboard', { replace: true });
            } else {
                console.debug('[RootRedirect] User not authenticated, redirecting to login');
                navigate('/admin/login', { replace: true });
            }
        }
    }, [user, loading, navigate]);

    return (
        <div className="loading-container">
            <div className="loading-spinner">Loading...</div>
        </div>
    );
};

function App() {
    console.debug('[App] Rendering...');
    return (
        <ErrorBoundary>
            <CartProvider>
                <Suspense fallback={<Loading />}>
                    <Routes>
                        {/* Public Routes */}
                        <Route path="/admin/login" element={<Login />} />
                        
                        {/* Protected Routes */}
                        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                            <Route path="/admin/dashboard" element={<Dashboard />} />
                            <Route path="/admin/products" element={<ProductManagement />} />
                            <Route path="/admin/categories" element={<CategoryManagement />} />
                            
                            {/* Inventory routes */}
                            <Route path="/admin/inventory" element={<Navigate to="/admin/inventory/stock" replace />} />
                            <Route path="/admin/inventory/stock" element={<StockManagement />} />
                            <Route path="/admin/inventory/counts" element={<InventoryCounts />} />
                            <Route path="/admin/inventory/adjustments" element={<InventoryAdjustments />} />
                            
                            {/* Transactions routes */}
                            <Route path="/admin/transactions" element={<Navigate to="/admin/transactions/sales" replace />} />
                            <Route path="/admin/transactions/sales" element={<SalesPurchases />} />
                            <Route path="/admin/transactions/purchases" element={<SalesPurchases />} />
                            <Route path="/admin/transactions/returns" element={<SalesPurchases />} />
                            
                            <Route path="/admin/users" element={<UserManagement />} />
                            <Route path="/admin/reports" element={<ReportsAnalytics />} />
                            <Route path="/admin/reports/stock" element={<StockReport />} />
                            <Route path="/admin/search" element={<SearchFilters />} />
                        </Route>

                        {/* Root route with smart redirect */}
                        <Route path="/admin" element={<RootRedirect />} />
                        
                        {/* Catch all route */}
                        <Route path="*" element={<Navigate to="/admin" replace />} />
                    </Routes>
                </Suspense>
            </CartProvider>
        </ErrorBoundary>
    );
}

export default App;